import { Injectable } from '@angular/core'
import { catchError, first, tap } from 'rxjs/operators'
import { ErrorSnackBarComponent } from '../utils/components/error-snack-bar/error-snack-bar.component'
import { Observable, of } from 'rxjs'
import { C4InvoicesRequest, CancellationInfo, HttpService, RevocationInfo } from '@api/http.service'
import { MatSnackBar } from '@angular/material/snack-bar'
import { NGXLogger } from 'ngx-logger'
import { Router } from '@angular/router'
import { environment } from 'src/environments/environment'
import { SnackBarData } from '../utils/components/error-snack-bar/data'
import { CancellationDialogComponent } from '../orders/orders-dashboard/orders-table/cancellation-dialog/cancellation-dialog.component'
import { InfoSnackBarComponent } from '../utils/components/info-snack-bar/info-snack-bar.component'
import { SuccessSnackBarComponent } from '../utils/components/success-snack-bar/success-snack-bar.component'
import { OrderInfoActions } from '../orders/data/data.actions'
import { MatDialog } from '@angular/material/dialog'
import { Store } from '@ngrx/store'
import { OrderElement } from '../orders/data/orderelement'

@Injectable({
  providedIn: 'root',
})
export class ApiService {
  readonly environment = environment

  constructor(
    private readonly http: HttpService,
    private readonly snackbar: MatSnackBar,
    private readonly logger: NGXLogger,
    private readonly router: Router,
    private readonly dialog: MatDialog,
    private readonly store: Store,
  ) {}

  redirectToOnlineSubscriptions(): void {
    if (this.environment.digitalContent) {
      this.router.navigateByUrl('/navigation/digital-content')
    } else {
      this.redirectToExternalWebReader()
    }
  }

  redirectToExternalWebReader(): void {
    if (typeof this.environment.onlineSubscriptionLink === 'string') {
      window.location.href = this.environment.onlineSubscriptionLink
    } else {
      this.router.navigateByUrl('/navigation/digital-content-links')
    }
  }

  downloadArticle(downloadUrl: string): Observable<void> {
    return this.http.downloadArticle(downloadUrl).pipe(
      first(),
      tap(response => {
        const a = document.createElement('a')
        // a.target = '_blank'
        const filename = downloadUrl.split('/').pop()
        a.download = filename || ''
        a.href = response.parameter
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
      }),
      catchError(error => {
        this.logger.error('Error while file trying to generate article download link')
        this.snackbar.openFromComponent(ErrorSnackBarComponent, {
          duration: 5000,
          panelClass: ['error-snack-bar'],
        })
        return of(error)
      }),
    )
  }

  downloadInvoice(sessionId: string, attributeId: string): Observable<void> {
    return this.http
      .getInvoiceDownloadLinks({
        sessionId: sessionId,
        attributeId: attributeId,
      } as C4InvoicesRequest)
      .pipe(
        first(),
        tap(data => {
          if (!data.body || data.body?.size === 0) {
            this.snackbar.openFromComponent(InfoSnackBarComponent, {
              duration: 5000,
              panelClass: ['info-snack-bar'],
              data: {
                title: 'INFO.NO_INVOICE_FOUND',
              } as SnackBarData,
            })
          } else {
            // Get file name
            let contentDisposition = data.headers.get('Content-Disposition')
            // Content Disposition example: "attachment; filename="Test.pdf""
            let re = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/
            let matches = contentDisposition?.match(re)
            let filename = matches && matches[1] ? matches[1].replace(/['"]/g, '') : 'Rechnung.pdf'

            let file = new Blob([data.body], { type: 'application/pdf' })
            let fileURL = URL.createObjectURL(file)

            // Download PDF in browser
            var a = document.createElement('a')
            a.href = fileURL
            a.download = filename
            document.body.appendChild(a)
            a.click()
            document.body.removeChild(a)
          }
        }),
        catchError(error => {
          this.logger.error('Error while file trying to generate download links')
          this.snackbar.openFromComponent(ErrorSnackBarComponent, {
            duration: 5000,
            panelClass: ['error-snack-bar'],
          })
          return of(error)
        }),
      )
  }

  getCancellationDialog(): Observable<string> {
    return this.dialog.open(CancellationDialogComponent).afterClosed()
  }

  cancelSubscription(attributeId: string, attributeType: string): Observable<void> {
    return this.http
      .cancelSubscription({
        attributeId: attributeId,
        attributeType: attributeType,
      } as CancellationInfo)
      .pipe(
        first(),
        tap(() => {
          this.snackbar.openFromComponent(SuccessSnackBarComponent, {
            panelClass: ['success-snack-bar'],
            data: {
              title: 'INFO.SUCCESSFUL_CANCELLATION',
            } as SnackBarData,
          })
          this.store.dispatch(OrderInfoActions.orderInfoLoad({ payload: '' }))
        }),
        catchError(err => {
          this.snackbar.openFromComponent(ErrorSnackBarComponent, {
            duration: 5000,
            panelClass: ['error-snack-bar'],
          })
          return of(err)
        }),
      )
  }

  revocateSubscription(order: OrderElement): Observable<void> {
    return this.http.revocateSubscription(order).pipe(
      first(),
      tap(() => {
        this.snackbar.openFromComponent(SuccessSnackBarComponent, {
          panelClass: ['success-snack-bar'],
          data: {
            title: 'INFO.SUCCESSFUL_REVOCATION',
          } as SnackBarData,
        })
        this.store.dispatch(OrderInfoActions.orderInfoLoad({ payload: '' }))
      }),
      catchError(err => {
        this.snackbar.openFromComponent(ErrorSnackBarComponent, {
          duration: 5000,
          panelClass: ['error-snack-bar'],
        })
        return of(err)
      }),
    )
  }
}
